import { useEffect, useRef, useState } from "react";
import { getDetailViewPanelId } from "../utils/detailViews.js";
import {
  RESULTS_SHELL_NAVIGATION_EVENT,
  emitResultsDetailViewChange,
  getDetailViewFromLocation,
  normalizeDetailViewName,
  syncDetailViewLocation,
} from "../utils/resultsDetailViewNavigation.js";

function scrollToDetailPanel(viewName) {
  const panelId = getDetailViewPanelId(viewName);
  const panel = panelId ? document.getElementById(panelId) : null;
  if (!(panel instanceof HTMLElement)) {
    return;
  }

  window.requestAnimationFrame(() => {
    panel.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

export default function useDetailViewState({ forcedViewName = "", initialResultsData }) {
  const normalizedForcedViewName = normalizeDetailViewName(forcedViewName);
  const [activeView, setActiveView] = useState(normalizedForcedViewName || "fleet");
  const [ready, setReady] = useState(false);
  const activeViewRef = useRef(null);
  const pendingScrollViewRef = useRef(null);
  const detailViewSubscribersRef = useRef(new Set());
  const detailViewsRef = useRef({});

  const notifyActiveViewChange = (viewName) => {
    detailViewSubscribersRef.current.forEach((callback) => {
      try {
        callback(viewName || null);
      } catch (error) {
        console.error("Impossible de notifier la vue détail active.", error);
      }
    });
  };

  const activateView = (viewName, options = {}) => {
    const normalizedViewName = normalizeDetailViewName(viewName);
    const panelId = getDetailViewPanelId(normalizedViewName || "");
    if (!panelId) {
      detailViewsRef.current.hideAllPanels(options);
      return false;
    }

    if (options.syncLocation !== false) {
      const requestedHistoryMode = options.historyMode === "push" && activeViewRef.current !== normalizedViewName
        ? "push"
        : "replace";
      syncDetailViewLocation(normalizedViewName, { historyMode: requestedHistoryMode });
    }

    if (options.scroll) {
      pendingScrollViewRef.current = normalizedViewName;
      if (activeViewRef.current === normalizedViewName) {
        scrollToDetailPanel(normalizedViewName);
      }
    } else {
      pendingScrollViewRef.current = null;
    }

    setActiveView(normalizedViewName);

    return true;
  };

  const hideAllPanels = (options = {}) => {
    if (options.syncLocation !== false) {
      const requestedHistoryMode = options.historyMode === "push" && activeViewRef.current
        ? "push"
        : "replace";
      syncDetailViewLocation(null, { historyMode: requestedHistoryMode });
    }

    pendingScrollViewRef.current = null;
    setActiveView(null);
  };

  const toggleView = (viewName, options = {}) => {
    if (activeViewRef.current === viewName) {
      hideAllPanels(options);
      return;
    }

    activateView(viewName, options);
  };

  detailViewsRef.current.activateView = activateView;
  detailViewsRef.current.getActiveView = () => activeViewRef.current;
  detailViewsRef.current.hideAllPanels = hideAllPanels;
  detailViewsRef.current.subscribe = (callback) => {
    if (typeof callback !== "function") {
      return () => {};
    }

    detailViewSubscribersRef.current.add(callback);
    callback(activeViewRef.current || null);
    return () => {
      detailViewSubscribersRef.current.delete(callback);
    };
  };
  detailViewsRef.current.sync = () => {
    notifyActiveViewChange(activeViewRef.current);
  };
  detailViewsRef.current.toggleView = toggleView;

  useEffect(() => {
    activeViewRef.current = activeView;
    notifyActiveViewChange(activeView);

    if (activeView && pendingScrollViewRef.current === activeView) {
      pendingScrollViewRef.current = null;
      scrollToDetailPanel(activeView);
    }

    if (!activeView) {
      pendingScrollViewRef.current = null;
    }

    if (ready) {
      emitResultsDetailViewChange({
        activeView,
        pathname: window.location.pathname,
      });
    }
  }, [activeView, ready]);

  useEffect(() => {
    if (!initialResultsData) {
      detailViewSubscribersRef.current.clear();
      setActiveView(null);
      setReady(false);
      return undefined;
    }

    const handleLocationChange = () => {
      const nextView = normalizedForcedViewName || getDetailViewFromLocation(window.location);
      pendingScrollViewRef.current = nextView || null;
      setActiveView(nextView || "fleet");
    };

    handleLocationChange();
    setReady(true);

    const handleShellNavigationRequest = (event) => {
      const requestedView = normalizeDetailViewName(event?.detail?.viewName);
      const navigationOptions = {
        historyMode: event?.detail?.historyMode === "push" ? "push" : "replace",
        syncLocation: event?.detail?.syncLocation !== false,
      };

      if (!requestedView) {
        hideAllPanels(navigationOptions);
        return;
      }

      activateView(requestedView, {
        ...navigationOptions,
        scroll: event?.detail?.scroll !== false,
      });
    };

    window.addEventListener("hashchange", handleLocationChange);
    window.addEventListener("popstate", handleLocationChange);
    window.addEventListener(RESULTS_SHELL_NAVIGATION_EVENT, handleShellNavigationRequest);

    return () => {
      window.removeEventListener("hashchange", handleLocationChange);
      window.removeEventListener("popstate", handleLocationChange);
      window.removeEventListener(RESULTS_SHELL_NAVIGATION_EVENT, handleShellNavigationRequest);
      setReady(false);
    };
  }, [initialResultsData, normalizedForcedViewName]);

  return {
    activeView,
    detailViews: detailViewsRef.current,
    ready,
    activateView,
    hideAllPanels,
    toggleView,
  };
}
